import dayjs from 'dayjs';

import type { PaymentHistoryPageViewDto, PaymentHistoryViewDto } from '@subscriptions/application/dtos';
import { formatCurrencyMXN } from '@subscriptions/application/utils';
import { redactRfc } from '@subscriptions/domain/value-objects';
import type {
  PaymentApiResponse,
  PaymentListApiResponse,
  RelatedInvoiceApiResponse,
} from '@subscriptions/infrastructure/api/types';

/** Mapper type for payment history API transformations */
export type PaymentHistoryApiMapperType = {
  mapApiResponseToViewDto: (response: PaymentApiResponse) => PaymentHistoryViewDto;
  mapApiResponseListToPage: (response: PaymentListApiResponse) => PaymentHistoryPageViewDto;
};

const CONCEPT_DISCOUNT_KEY = 'discounts';
const CONCEPT_FINANCING_KEY = 'financing';
const CONCEPT_FOLIOS_KEY = 'folios';
const CONCEPT_PLAN_KEY = 'plan';

/** Returns the concept value for a key, or null when absent. */
const findConceptValue = (concepts: PaymentApiResponse['concepts'], key: string): number | null => {
  const match = concepts.find((concept) => concept.key === key);
  return match ? match.value : null;
};

/** Formats a numeric value as MXN currency, returning empty string when null/undefined. */
const formatOptionalAmount = (value: number | null): string => (value === null ? '' : formatCurrencyMXN(value));

/**
 * Reads the invoicing failure reason, which the API nests inside `invoiceData`.
 *
 * Redacted on the way in: the PAC quotes the taxpayer's RFC in several of these messages, and the
 * retry link carries them in a query param that access logs capture in full (Charter §5.1). The
 * sheet re-inserts the RFC from the fiscal record it already holds, so nothing is lost on screen.
 */
const readErrorMessage = (response: PaymentApiResponse): string | null =>
  response.invoiceData?.errorMessage ? redactRfc(response.invoiceData.errorMessage) : null;

const DISPLAY_DATE_FORMAT = 'DD/MM/YYYY';

/**
 * Formats an API date into the `DD/MM/YYYY` the view DTO carries.
 *
 * An ISO timestamp is rendered in the browser's timezone, which is what the fiscal calendar
 * this feeds means: a charge stamped `2026-09-01T02:00:00Z` happened on August 31st in Mexico,
 * and the re-invoicing window has to read it as August.
 */
const formatDate = (raw: string | null | undefined): string => {
  if (!raw) return '';
  const parsed = dayjs(raw);
  return parsed.isValid() ? parsed.format(DISPLAY_DATE_FORMAT) : '';
};

/**
 * Creates mapper for payment history API type conversions.
 *
 * - `paymentDate` is formatted as DD/MM/YYYY ('' when empty or unparseable).
 * - `createdAt` is the same format, read from the charge's own timestamp rather than from
 *   `paymentDate` — which names the period being charged and runs ahead of it. It is never
 *   displayed; it is what the re-invoicing window is measured against.
 * - `subscription` comes from `subscriptionName` when present.
 * - `discount`, `financing`, `folios`, and `license` are read from the `concepts` array by key
 *   (`discounts`, `financing`, `folios`, `plan`) and formatted as MXN currency.
 * - `license` maps from the `plan` concept (the backend uses `plan` for the subscription license amount).
 * - `folio` (singular) maps from `invoiceData.folio` and is only used to name the downloaded file.
 * - `invoiceDataUuid` identifies the fiscal record the payment was billed against; child rows
 *   inherit the parent's, since per-store invoices share the payment's fiscal record.
 * - `invoiceStatus` and `errorMessage` describe the *invoice*, independently of the payment
 *   `status`, and are read per row rather than inherited: a grouped payment puts the CFDI on the
 *   parent when `group_invoice` is on, and one per child when it is off, so only the row that
 *   owns an invoice reports its outcome.
 * - `subscriptionAmount` is the `total` parsed and formatted as MXN currency.
 * Empty strings are preserved so the table fallback ("-") remains consistent.
 */
export const createPaymentHistoryApiMapper = (): PaymentHistoryApiMapperType => {
  /**
   * Maps a related invoice (per-store) to a child PaymentHistoryViewDto.
   * Inherits date/method/status/concept from the parent payment, but uses the
   * store name, own invoice URLs, folio (fallback ''), and own total/concepts.
   */
  const mapRelatedInvoiceToChild = (
    parent: PaymentApiResponse,
    related: RelatedInvoiceApiResponse,
  ): PaymentHistoryViewDto => {
    const discountValue = findConceptValue(related.concepts, CONCEPT_DISCOUNT_KEY);
    const financingValue = findConceptValue(related.concepts, CONCEPT_FINANCING_KEY);
    const foliosValue = findConceptValue(related.concepts, CONCEPT_FOLIOS_KEY);
    const licenseValue = findConceptValue(related.concepts, CONCEPT_PLAN_KEY);

    return {
      uuid: `${parent.uuid}:${related.storeUuid}`,
      // The row is keyed per store, but it settles under the parent payment — which is the only
      // thing the API can be asked to act on.
      paymentUuid: parent.uuid,
      paymentDate: formatDate(parent.paymentDate),
      // A per-store row is not charged on its own, so the window it can be re-invoiced in is
      // the parent charge's.
      createdAt: formatDate(parent.createdAt),
      subscription: related.storeName,
      concept: parent.concept ?? '',
      status: parent.status,
      paymentMethod: parent.paymentMethod?.methodType ?? '',
      paymentMethodDetail: parent.paymentMethod?.methodDetail ?? '',
      subscriptionAmount: formatOptionalAmount(related.total),
      license: formatOptionalAmount(licenseValue),
      folios: formatOptionalAmount(foliosValue),
      financing: formatOptionalAmount(financingValue),
      discount: formatOptionalAmount(discountValue),
      folio: related.folio ?? '',
      pdfUrl: related.pdfUrl,
      xmlUrl: related.xmlUrl,
      // The fiscal record is the payment's, so it is inherited — but the invoicing outcome is
      // this store's own and is left null when the group stamped a single CFDI on the parent.
      invoiceDataUuid: parent.invoiceDataUuid ?? null,
      blockUuid: parent.subscriptionUuid ?? null,
      invoiceStatus: related.status ?? null,
      errorMessage: related.errorMessage ? redactRfc(related.errorMessage) : null,
    };
  };

  const mapApiResponseToViewDto = (response: PaymentApiResponse): PaymentHistoryViewDto => {
    const discountValue = findConceptValue(response.concepts, CONCEPT_DISCOUNT_KEY);
    const financingValue = findConceptValue(response.concepts, CONCEPT_FINANCING_KEY);
    const foliosValue = findConceptValue(response.concepts, CONCEPT_FOLIOS_KEY);
    const licenseValue = findConceptValue(response.concepts, CONCEPT_PLAN_KEY);
    const totalNumber = Number(response.total);
    const relatedInvoices = response.relatedInvoices?.map((related) => mapRelatedInvoiceToChild(response, related));

    return {
      uuid: response.uuid,
      paymentDate: formatDate(response.paymentDate),
      createdAt: formatDate(response.createdAt),
      subscription: response.subscriptionName ?? '',
      concept: response.concept ?? '',
      status: response.status,
      paymentMethod: response.paymentMethod?.methodType ?? '',
      paymentMethodDetail: response.paymentMethod?.methodDetail ?? '',
      subscriptionAmount: Number.isFinite(totalNumber) ? formatCurrencyMXN(totalNumber) : '',
      license: formatOptionalAmount(licenseValue),
      folios: formatOptionalAmount(foliosValue),
      financing: formatOptionalAmount(financingValue),
      discount: formatOptionalAmount(discountValue),
      folio: response.invoiceData?.folio ?? '',
      pdfUrl: response.invoiceData?.pdfUrl ?? null,
      xmlUrl: response.invoiceData?.xmlUrl ?? null,
      paymentUuid: response.uuid,
      invoiceDataUuid: response.invoiceDataUuid ?? null,
      blockUuid: response.subscriptionUuid ?? null,
      invoiceStatus: response.invoiceData?.status ?? null,
      errorMessage: readErrorMessage(response),
      ...(relatedInvoices && relatedInvoices.length > 0 ? { relatedInvoices } : {}),
    };
  };

  const mapApiResponseListToPage = (response: PaymentListApiResponse): PaymentHistoryPageViewDto => ({
    items: response.data.map(mapApiResponseToViewDto),
    pagination: {
      next: response.pagination.next,
      previous: response.pagination.previous,
    },
  });

  return { mapApiResponseToViewDto, mapApiResponseListToPage };
};
